/**
 * Algolia Cross-Page Tracking Composable
 * 
 * Stores queryID and objectID when users click products from search results,
 * allowing conversion tracking (add to cart, purchase) on subsequent pages.
 * 
 * According to Algolia docs:
 * "Conversion events often happen outside the search results page.
 * To associate a conversion with the correct search query, save this query ID 
 * and include it in your conversion events."
 * 
 * @see https://www.algolia.com/doc/guides/sending-events/api-client/send-ecommerce-events#track-query-ids-across-pages
 */

export interface AlgoliaTrackingData {
  queryID: string;
  objectID: string;
  timestamp: number;
}

export default function useAlgoliaTracking() {
  const STORAGE_KEY = '_algolia_tracking';
  // No TTL - tracking persists until browser tab closes

  /**
   * Store tracking data when user clicks a product from search
   */
  const storeProductTracking = (params: {
    objectID: string;
    queryID: string;
  }) => {
    if (!process.client) return;

    const data: AlgoliaTrackingData = {
      queryID: params.queryID,
      objectID: params.objectID,
      timestamp: Date.now(),
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      console.log('[Algolia Tracking] Stored:', data);
    } catch (error) {
      console.error('[Algolia Tracking] Failed to store:', error);
    }
  };

  /**
   * Get stored tracking data
   * Returns the stored queryID and objectID (persists until tab closes)
   */
  const getTrackingData = (): AlgoliaTrackingData | null => {
    if (!process.client) return null;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data: AlgoliaTrackingData = JSON.parse(stored);
      return data;
    } catch (error) {
      console.error('[Algolia Tracking] Failed to retrieve:', error);
      return null;
    }
  };

  /**
   * Get stored queryID (convenience method)
   */
  const getQueryID = (): string | null => {
    const data = getTrackingData();
    return data?.queryID || null;
  };

  /**
   * Get stored objectID (convenience method)
   */
  const getObjectID = (): string | null => {
    const data = getTrackingData();
    return data?.objectID || null;
  };

  /**
   * Get stored tracking data for a specific objectID
   * Returns queryID if objectID matches, null otherwise
   */
  const getProductTracking = (objectID: string): string | null => {
    const data = getTrackingData();
    
    if (!data) return null;

    // Check if matches current product
    if (data.objectID === objectID) {
      return data.queryID;
    }

    return null;
  };

  /**
   * Clear stored tracking data
   */
  const clearProductTracking = () => {
    if (!process.client) return;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('[Algolia Tracking] Failed to clear:', error);
    }
  };

  return {
    storeProductTracking,
    getTrackingData,
    getQueryID,
    getObjectID,
    getProductTracking,
    clearProductTracking,
  };
}
